import { Popper } from "@mui/material";
import { EllipsisVertical } from "@procore/core-icons";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useAppSelector } from "app/redux-store-interactions";
import { ToolbarButtonEntry, toolbarsActions, useDispatchToolbars } from "contexts/toolbars";
import { ProcoreToolMode, selectActiveToolMode } from "slices/procoreUx";

import { QUICK_ACCESS_TOOLBAR } from "../constants";
import { Toolbar } from "../Toolbar";
import { useFlyoutRegistration } from "../useFlyoutRegistration";
import { useToolsButtons } from "./useToolsButtons";
import { flyOutMenuHandler, TOOLS_BUTTON_IDS } from "./utils";

interface OverflowFlyoutToolbarProps {
    id: string;
    primaryToolbarId: string;
    onClose: () => void;
}

function OverflowFlyoutToolbar({ id, primaryToolbarId, onClose }: OverflowFlyoutToolbarProps) {
    const toolbarDispatch = useDispatchToolbars();
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;

    const handleToolSelect = useCallback(() => onCloseRef.current(), []);
    const { buttons: toolButtons, isMeasureActive, isSectionActive } = useToolsButtons(handleToolSelect);

    // In the flyout, tool buttons are only shown when NOT active (the active tool
    // is already displayed in CurrentModeToolbar), and never shown as active.
    const flyoutToolButtons: ToolbarButtonEntry[] = useMemo(
        () =>
            toolButtons.map((btn) => ({
                ...btn,
                state: {
                    ...btn.state,
                    active: false,
                    visible: btn.config.id === TOOLS_BUTTON_IDS.MEASURE ? !isMeasureActive : !isSectionActive,
                },
            })),
        [toolButtons, isMeasureActive, isSectionActive],
    );

    useEffect(() => {
        toolbarDispatch(toolbarsActions.addToolbar({ id, buttons: flyoutToolButtons }));

        const disposeMenuHandler = flyOutMenuHandler({
            flyoutMenuId: id,
            toolbarId: primaryToolbarId,
            close: () => onCloseRef.current(),
        });

        return () => {
            disposeMenuHandler();
            toolbarDispatch(toolbarsActions.removeToolbar(id));
        };
    }, [toolbarDispatch, id, primaryToolbarId, flyoutToolButtons]);

    return <Toolbar id={id} variant="fly-out" />;
}

interface OverflowToolbarProps {
    id?: string;
}

/**
 * Toolbar with a single overflow (ellipsis) button shown when a current-mode picker is active.
 * Opens a flyout with view tool buttons (camera, render modes, x-ray) plus the inactive
 * tool-switching buttons (measure/section).
 */
export function OverflowToolbar({ id = "overflow-toolbar" }: OverflowToolbarProps) {
    const toolbarDispatch = useDispatchToolbars();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    useFlyoutRegistration(isMenuOpen);

    const ellipsisBtnRef = useRef<HTMLButtonElement>(null);
    const primaryToolbarId = `${id}-primary`;
    const flyoutId = `${id}-flyout`;

    const activeToolMode = useAppSelector(selectActiveToolMode);
    const isVisible = activeToolMode !== ProcoreToolMode.Default;

    const handleClose = useCallback(() => {
        setIsMenuOpen(false);
    }, []);

    useEffect(() => {
        const buttons: ToolbarButtonEntry[] = [
            {
                config: {
                    id: "overflow-toggle",
                    icon: <EllipsisVertical />,
                    onClick: () => setIsMenuOpen((open) => !open),
                    title: "More Tools",
                    ref: ellipsisBtnRef,
                    tooltip: {
                        toolName: "moreTools",
                        shortcutKey: "",
                        alwaysVisible: false,
                        placement: "left",
                    },
                },
                state: {
                    active: isMenuOpen,
                    disabled: false,
                    visible: isVisible,
                },
            },
        ];

        toolbarDispatch(toolbarsActions.addToolbar({ id: primaryToolbarId, buttons }));

        return () => {
            toolbarDispatch(toolbarsActions.removeToolbar(primaryToolbarId));
        };
    }, [toolbarDispatch, primaryToolbarId, isMenuOpen, isVisible]);

    useEffect(() => {
        toolbarDispatch(
            toolbarsActions.bulkUpdateButtonStates(primaryToolbarId, {
                "overflow-toggle": { active: isMenuOpen, visible: isVisible },
            }),
        );
    }, [toolbarDispatch, primaryToolbarId, isMenuOpen, isVisible]);

    useEffect(() => {
        if (!isVisible) {
            setIsMenuOpen(false);
        }
    }, [isVisible]);

    return (
        <>
            <Toolbar id={primaryToolbarId} />
            <Popper
                open={isMenuOpen}
                anchorEl={ellipsisBtnRef.current}
                placement="left-start"
                sx={{ zIndex: QUICK_ACCESS_TOOLBAR.FLYOUT_Z_INDEX }}
            >
                <OverflowFlyoutToolbar id={flyoutId} primaryToolbarId={primaryToolbarId} onClose={handleClose} />
            </Popper>
        </>
    );
}
